/**
 * Vincent #292 (2026-06-12) — Vue minimale apprenant d'une vidéo de parcours.
 *
 * Affichée quand un utilisateur NON AUTORISÉ à éditer un parcours clique sur
 * la vidéo (depuis PathwayLearnerModal). Comporte uniquement :
 *   - le lecteur vidéo (pas de transcript, pas de Q&R, pas de Scripts, …)
 *   - un heartbeat "vu ≥ 30 s" identique à VideoDetailPage (watch-progress)
 *   - un bouton "Faire le quiz" qui pointe sur /quiz?video_id=...
 *   - un lien de retour vers le parcours d'origine
 *
 * Vincent #297 — Cette même page est utilisée pour le bouton "Visualiser"
 * (éditeur), de manière à présenter le parcours tel qu'il apparaît aux
 * apprenants (Mode de démonstration).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, PlayCircle, SpinnerGap } from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import useStreamToken from "../hooks/useStreamToken";

const API = process.env.REACT_APP_BACKEND_URL;
function L(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function LearnerVideoPage() {
  const { videoId } = useParams();
  const [searchParams] = useSearchParams();
  const pathwayId = searchParams.get("pathway_id") || "";
  const demoMode = searchParams.get("demo") === "1";
  const navigate = useNavigate();
  const { lang } = useLang();

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [thresholdReached, setThresholdReached] = useState(false);
  // Vincent #394 (Eric 2026-07-01 SPEC STRICK — À REFAIRE) — Heartbeat périodique
  // pour propager la durée max réellement visionnée au backend (au lieu d'un
  // envoi unique à 30 s qui restait figé). Le backend fait max(prev, new_seconds).
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const lastSentSecondsRef = useRef(0);
  const videoRef = useRef(null);
  const watchProgressSentRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/videos/${videoId}`, { withCredentials: true });
        if (cancelled) return;
        setVideo(r.data?.video || r.data);
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        setError(typeof detail === "string" ? detail : L(lang, "Vidéo introuvable", "Video not found"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [videoId, lang]);

  // Vincent #394 (Eric 2026-07-04 SPEC STRICK) — Initialisation depuis backend :
  // si watched_ok=true en base (visite précédente), afficher immédiatement le
  // pill "Visionnage comptabilisé" sans attendre 30 s de lecture cette session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/videos/my-watch-progress`, { withCredentials: true });
        if (cancelled) return;
        const prog = data && data[videoId];
        if (prog?.watched_ok) {
          setThresholdReached(true);
          watchProgressSentRef.current = true; // évite un re-POST au 1er heartbeat
        }
        if (prog?.seconds_watched && prog.seconds_watched > 0) {
          setWatchedSeconds(prog.seconds_watched);
          lastSentSecondsRef.current = prog.seconds_watched;
        }
      } catch (_) { /* non-bloquant */ }
    })();
    return () => { cancelled = true; };
  }, [videoId]);

  const streamUrl = useStreamToken({ videoId, api: API });

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const t = v.currentTime;
    // Vincent #394 (Eric 2026-07-01 SPEC STRICK) — 3 comportements :
    // 1) MAJ immédiate de l'état local `watchedSeconds` (affichage % en direct)
    // 2) 1er heartbeat à 30 s (débloque le badge "≥ 30 s")
    // 3) Heartbeat additionnel tous les +15 s pour propager la durée max
    if (t > watchedSeconds) setWatchedSeconds(t);
    const shouldSend =
      (!watchProgressSentRef.current && t >= 30) ||
      (watchProgressSentRef.current && t - lastSentSecondsRef.current >= 15);
    if (shouldSend) {
      const wasFirst = !watchProgressSentRef.current;
      watchProgressSentRef.current = true;
      lastSentSecondsRef.current = t;
      axios.post(
        `${API}/api/videos/${videoId}/watch-progress`,
        { seconds: t },
        { withCredentials: true }
      )
        .then(() => { if (wasFirst) setThresholdReached(true); })
        .catch(() => {
          if (wasFirst) watchProgressSentRef.current = false;
        });
    }
  };

  const backLink = pathwayId
    ? `/dashboard/learning-path` // returns to learner pathways list (modal can be reopened)
    : "/dashboard";

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="learner-video-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <button
            onClick={() => navigate(backLink)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-[#002FA7]"
            data-testid="learner-video-back-btn"
          >
            <ArrowLeft size={16} weight="bold" />
            {L(lang, "Retour au parcours", "Back to pathway")}
          </button>
          {demoMode && (
            <span
              className="px-2.5 py-1 bg-amber-100 border border-amber-200 text-amber-800 text-xs font-bold rounded-full"
              data-testid="learner-video-demo-banner"
            >
              {L(lang, "Mode de démonstration", "Demo mode")}
            </span>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center">
            <SpinnerGap size={28} className="animate-spin text-[#002FA7] mx-auto mb-2" />
            <p className="text-sm text-zinc-500">{L(lang, "Chargement…", "Loading…")}</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-rose-200 p-8 text-center" data-testid="learner-video-error">
            <p className="text-sm text-rose-700 font-semibold">{error}</p>
          </div>
        ) : (
          <>
            <div className="bg-black rounded-xl overflow-hidden shadow-lg">
              <video
                ref={videoRef}
                src={streamUrl}
                controls
                onTimeUpdate={handleTimeUpdate}
                className="w-full aspect-video"
                data-testid="learner-video-player"
              />
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 mt-4 p-5">
              <h1 className="text-xl font-bold text-zinc-900 mb-1" data-testid="learner-video-title">
                {video?.title}
              </h1>
              {video?.training_theme && (
                <p className="text-xs text-zinc-500 mb-3">{video.training_theme}</p>
              )}

              <div className="flex flex-wrap items-center gap-3 mt-2">
                <Link
                  to={`/dashboard/quiz?video_id=${videoId}&auto=1`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-sm shadow hover:shadow-lg transition-all"
                  data-testid="learner-video-quiz-btn"
                >
                  <PlayCircle size={18} weight="fill" />
                  {L(lang, "Faire le quiz sur cette vidéo", "Take the quiz on this video")}
                </Link>

                {thresholdReached && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs text-teal-700 bg-teal-50 border border-teal-200 px-2.5 py-1 rounded-full font-semibold"
                    data-testid="learner-video-watched-pill"
                  >
                    <CheckCircle size={14} weight="fill" />
                    {L(lang, "Visionnage comptabilisé (≥ 30 s)", "Watch counted (≥ 30 s)")}
                  </span>
                )}
              </div>

              <p className="mt-4 text-[11px] text-zinc-400">
                {L(
                  lang,
                  "Une vidéo est complétée après ≥ 30 s de visionnement ET un quiz de 3+ questions réussi.",
                  "A video is completed after ≥ 30s watched AND a quiz of 3+ questions taken."
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
